import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { reportsAPI } from '../../services/api';
import DateSelector from '../Common/DateSelector';
import MetricCard from '../Common/MetricCard';
import ComboChart from '../Charts/ComboChart';
import MapView from '../Maps/MapView';
import DashboardHeader from '../Common/DashboardHeader';
import TelevisionTable from '../Tables/TelevisionTable';
import RadioTable from '../Tables/RadioTable';
import SportSignageTable from '../Tables/SportSignageTable';
import LazyLoad from '../Common/LazyLoad';
import TabNavigation, { Tab } from '../Common/TabNavigation';
import UserManagementView from '../Admin/UserManagementView';
import CoverageAnalysisView from '../Admin/CoverageAnalysisView';
import { CHANNEL_COLORS } from '../../types';
import { hasChartData, hasTableData } from '../../utils/dataValidation';
import KnowledgePanel from '../Common/KnowledgePanel';
import { getDefinition } from '../../data/advertisingDefinitions';
import './dashboard-common.css';
import './RegionalManagerDashboard.css';

interface DistrictData {
  name: string;
  manager: string;
  dealerCount: number;
  impressions: number;
  clicks: number;
  ctr: number;
  store_visits: number;
  trend: number[];
  channels?: Record<string, number>;
  dealers?: {
    name: string;
    dealer_id?: number;
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
    zip: string;
  }[];
}

interface RegionalMetrics {
  totalImpressions: number;
  digitalImpressions: number;
  broadcastImpressions: number;
  totalClicks: number;
  totalStoreVisits: number;
  avgCTR: number;
  districtCount: number;
  dealerCount: number;
  trend?: number[];
}

interface CombinedMetrics {
  impressions: number;
  clicks: number;
  store_visits: number;
  ctr: number;
  trend?: number[];
}

const RegionalManagerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('all');

  const getDefaultStartDate = () => {
    // Campaign started September 1, 2025 - always use this as default start date
    return new Date(2025, 8, 1); // September 1, 2025 (month 8 = September)
  };

  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: getDefaultStartDate().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [districtData, setDistrictData] = useState<DistrictData[]>([]);
  const [regionalTotals, setRegionalTotals] = useState<{
    totalImpressions: number,
    digitalImpressions: number,
    broadcastImpressions: number,
    clicks: number
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'map' | 'chart'>('table');
  const [chartData, setChartData] = useState<any>({
    display: [],
    dooh: [],
    streaming_tv: [],
    streaming_audio: [],
    youtube: [],
    search: [],
    broadcast_tv: [],
    radio: [],
    paid_social: [],
    sport_signage: []
  });
  const [mapData, setMapData] = useState<any>({
    display: [],
    dooh: [],
    streaming_tv: [],
    youtube: [],
    search: []
  });
  const [dealerLocations, setDealerLocations] = useState<any[]>([]);
  const [spotsData, setSpotsData] = useState<any>(null);
  const [channelTotals, setChannelTotals] = useState<Record<string, number>>({});
  const [knowledgePanelOpen, setKnowledgePanelOpen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState<string>('');
  const [mlbSocialData, setMlbSocialData] = useState<{ views: number; engagement: number } | null>(null);

  const handleHelpClick = (topic: string) => {
    if (knowledgePanelOpen && currentTopic === topic) {
      setKnowledgePanelOpen(false);
    } else {
      setCurrentTopic(topic);
      setKnowledgePanelOpen(true);
    }
  };

  // Fetch district data for the region
  useEffect(() => {
    const fetchDistrictData = async () => {
      if (!user?.manages || user.manages.length === 0) {
        setError('No districts assigned to this region');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('[OPTIMIZATION #2] Regional: Using consolidated endpoint');
        const consolidatedStart = performance.now();

        // OPTIMIZATION #2: Single consolidated API call
        const [consolidated, socialData] = await Promise.all([
          reportsAPI.getConsolidatedDashboard(
            undefined, // No specific dealer - regional view
            dateRange.start,
            dateRange.end
          ),
          reportsAPI.getMlbSocialSummary(dateRange.start, dateRange.end).catch(() => null)
        ]);
        if (socialData) setMlbSocialData({ views: socialData.views || 0, engagement: socialData.engagement || 0 });

        const consolidatedEnd = performance.now();
        console.log(`[OPTIMIZATION #2] Regional consolidated endpoint took ${(consolidatedEnd - consolidatedStart).toFixed(2)}ms`);

        // Extract data from consolidated response
        const summary = consolidated.summary;
        const charts = consolidated.charts;
        const mapResponse = consolidated.map;
        const spotsData = consolidated.spots;

        // Process district data from summary
        if (summary.districts && Array.isArray(summary.districts)) {
          const districtData: DistrictData[] = summary.districts.map((district: any) => {
            const channels = district.channels || {};
            const totalImpressions = Object.values(channels).reduce((sum: number, ch: any) => sum + (ch.impressions || 0), 0);
            const totalClicks = Object.values(channels).reduce((sum: number, ch: any) => sum + (ch.clicks || 0), 0);
            const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

            // Flatten channels to {channelName: impressions} for easy table rendering
            const channelImpressions: Record<string, number> = {};
            Object.entries(channels).forEach(([k, v]: [string, any]) => {
              channelImpressions[k] = v?.impressions ?? 0;
            });

            return {
              name: `District ${district.district_manager?.split(',')[0] || 'Unknown'}`,
              manager: district.district_manager,
              dealerCount: district.dealer_count || 0,
              impressions: totalImpressions,
              clicks: totalClicks,
              ctr,
              store_visits: district.store_visits || 0,
              trend: [],
              channels: channelImpressions,
              dealers: []
            };
          });

          setDistrictData(districtData);

          // Store regional-level TRUE totals (unique impressions, not sum of districts)
          if (summary.total) {
            // Calculate three separate metrics
            let totalImpressions = 0;
            let digitalImpressions = 0;
            let broadcastImpressions = 0;
            let totalClicks = 0;

            Object.entries(summary.total).forEach(([channelName, channel]: [string, any]) => {
              const impressions = channel?.impressions || 0;
              const clicks = channel?.clicks || 0;

              // Add to total (all channels)
              totalImpressions += impressions;
              totalClicks += clicks;

              // Categorize into digital vs broadcast
              if (channelName === 'Broadcast TV' || channelName === 'Radio') {
                broadcastImpressions += impressions;
              } else {
                digitalImpressions += impressions;
              }
            });

            setRegionalTotals({
              totalImpressions,
              digitalImpressions,
              broadcastImpressions,
              clicks: totalClicks
            });

            // Store per-channel totals for individual metric cards
            const perChannel: Record<string, number> = {};
            Object.entries(summary.total).forEach(([channelName, channel]: [string, any]) => {
              perChannel[channelName] = channel?.impressions || 0;
            });
            setChannelTotals(perChannel);
          }
        } else {
          setDistrictData([]);
        }

        // Set chart data from consolidated response
        setChartData({
          display: charts.Display || [],
          dooh: charts.DOOH || [],
          streaming_tv: charts['Streaming TV'] || [],
          streaming_audio: charts['Streaming Audio'] || [],
          youtube: charts.YouTube || [],
          search: charts.Search || [],
          broadcast_tv: charts['Broadcast TV'] || [],
          radio: charts.Radio || [],
          paid_social: charts['Paid Social'] || [],
          sport_signage: charts['Sport Signage'] || []
        });

        // Transform map data for MapView component
        const transformedMapData: any = { display: [], dooh: [], streaming_tv: [] };

        if (mapResponse.zip_codes && Array.isArray(mapResponse.zip_codes)) {
          mapResponse.zip_codes.forEach((zipData: any) => {
            const coordinates: [number, number] | undefined =
              (zipData.longitude && zipData.latitude)
                ? [zipData.longitude, zipData.latitude]
                : undefined;

            if (zipData.channels && Array.isArray(zipData.channels)) {
              zipData.channels.forEach((channelData: any) => {
                const channelKey = channelData.channel.toLowerCase().replace(' ', '_');

                if (transformedMapData[channelKey]) {
                  transformedMapData[channelKey].push({
                    zip: zipData.zip_code,
                    impressions: channelData.impressions || 0,
                    coordinates: coordinates,
                    channels: zipData.channels,
                    city: zipData.city,
                    state: zipData.state
                  });
                }
              });
            }
          });
        }

        setMapData(transformedMapData);

        // Set dealer locations for map markers
        if (mapResponse.dealer_locations && Array.isArray(mapResponse.dealer_locations)) {
          setDealerLocations(mapResponse.dealer_locations.map((loc: any) => ({
            dealer_id: loc.dealer_id,
            name: loc.name,
            latitude: loc.latitude,
            longitude: loc.longitude,
            address: loc.address,
            city: loc.city,
            state: loc.state,
            zip: loc.zip
          })));
        } else {
          setDealerLocations([]);
        }

        // Set spots data from consolidated response
        setSpotsData(spotsData);

        console.log('[OPTIMIZATION #2] Regional dashboard loaded successfully from consolidated endpoint');

      } catch (err) {
        console.error('Error fetching district data:', err);
        setError('Failed to load district data');
      } finally {
        setLoading(false);
      }
    };

    if (dateRange.start && dateRange.end) {
      fetchDistrictData();
    }
  }, [user, dateRange]);

  // Calculate regional metrics
  const regionalMetrics = useMemo<RegionalMetrics>(() => {
    if (districtData.length === 0) {
      return {
        totalImpressions: 0,
        digitalImpressions: 0,
        broadcastImpressions: 0,
        totalClicks: 0,
        totalStoreVisits: 0,
        avgCTR: 0,
        districtCount: 0,
        dealerCount: 0
      };
    }

    // Use TRUE regional totals from backend (unique impressions, not sum of district overlaps)
    const totalImpressions = regionalTotals?.totalImpressions || 0;
    const digitalImpressions = regionalTotals?.digitalImpressions || 0;
    const broadcastImpressions = regionalTotals?.broadcastImpressions || 0;
    const clicks = regionalTotals?.clicks || 0;

    // Sum dealer count and store visits from districts
    const totals = districtData.reduce((acc, district) => ({
      store_visits: acc.store_visits + district.store_visits,
      dealers: acc.dealers + district.dealerCount
    }), { store_visits: 0, dealers: 0 });

    return {
      totalImpressions,
      digitalImpressions,
      broadcastImpressions,
      totalClicks: clicks,
      totalStoreVisits: totals.store_visits,
      avgCTR: digitalImpressions > 0 ? (clicks / digitalImpressions) * 100 : 0,
      districtCount: districtData.length,
      dealerCount: totals.dealers
    };
  }, [districtData, regionalTotals]);

  // Get data for selected district or all districts
  const selectedData = useMemo<CombinedMetrics>(() => {
    if (selectedDistrict === 'all') {
      return {
        impressions: regionalMetrics.totalImpressions,
        clicks: regionalMetrics.totalClicks,
        store_visits: regionalMetrics.totalStoreVisits,
        ctr: regionalMetrics.avgCTR,
        trend: regionalMetrics.trend
      };
    }
    const district = districtData.find(d => d.manager === selectedDistrict);
    if (district) {
      return {
        impressions: district.impressions,
        clicks: district.clicks,
        store_visits: district.store_visits,
        ctr: district.ctr,
        trend: district.trend
      };
    }
    return {
      impressions: 0,
      clicks: 0,
      store_visits: 0,
      ctr: 0,
      trend: []
    };
  }, [selectedDistrict, districtData, regionalMetrics]);

  // Handle district selection
  const handleDistrictSelect = useCallback((district: string) => {
    setSelectedDistrict(district);
  }, []);

  // Navigate to district dashboard
  const navigateToDistrict = useCallback((districtManager: string) => {
    // Find the district data for this manager
    const districtInfo = districtData.find(d => d.manager === districtManager);
    navigate('/district', { 
      state: { 
        districtId: districtManager, // Use the actual district manager name for API calls
        districtName: districtInfo?.name,
        districtManager: districtManager,
        regionContext: {
          regionManager: user?.manager_name || user?.username,
          regionId: user?.username?.replace(' ', '_').toLowerCase()
        }
      } 
    });
  }, [navigate, districtData, user]);

  // Format number with commas
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };


  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading regional data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="regional-dashboard error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Define tabs for navigation
  const tabs: Tab[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'dashboard',
      content: (
        <div className="dashboard-container regional-dashboard">
      <div className="dashboard-controls">
        <div className="controls-wrapper">
          <div className="filter-controls">
            <DateSelector 
              startDate={dateRange.start ? new Date(dateRange.start) : new Date()}
              endDate={dateRange.end ? new Date(dateRange.end) : new Date()}
              onDateChange={(start: Date, end: Date) => {
                setDateRange({
                  start: start.toISOString().split('T')[0],
                  end: end.toISOString().split('T')[0]
                });
              }} 
            />
            <div className="district-selector">
              <select 
                value={selectedDistrict} 
                onChange={(e) => handleDistrictSelect(e.target.value)}
                className="district-dropdown"
              >
                <option value="all">All Districts (Regional Total)</option>
                {districtData.map(district => (
                  <option key={district.manager} value={district.manager}>
                    {district.name} - {district.manager}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="view-toggle">
              <button 
                className={viewMode === 'table' ? 'active' : ''}
                onClick={() => setViewMode('table')}
              >
                <i className="material-icons">table_chart</i>
                Table View
              </button>
              <button 
                className={viewMode === 'map' ? 'active' : ''}
                onClick={() => setViewMode('map')}
              >
                <i className="material-icons">map</i>
                Map View
              </button>
              <button 
                className={viewMode === 'chart' ? 'active' : ''}
                onClick={() => setViewMode('chart')}
              >
                <i className="material-icons">show_chart</i>
                Chart View
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="container">

      <div className="metrics-grid">
        {(channelTotals['Display'] > 0) && (
          <MetricCard title="Display" value={channelTotals['Display']} color={CHANNEL_COLORS.display} onHelpClick={() => handleHelpClick('display')} />
        )}
        {(channelTotals['DOOH'] > 0) && (
          <MetricCard title="DOOH" value={channelTotals['DOOH']} color={CHANNEL_COLORS.dooh} hideClicks={true} onHelpClick={() => handleHelpClick('dooh')} />
        )}
        {(channelTotals['Streaming TV'] > 0) && (
          <MetricCard title="Streaming TV" value={channelTotals['Streaming TV']} color={CHANNEL_COLORS.streaming_tv} hideClicks={true} onHelpClick={() => handleHelpClick('streaming_tv')} />
        )}
        {(channelTotals['Broadcast TV'] > 0) && (
          <MetricCard title="Broadcast TV" value={channelTotals['Broadcast TV']} color={CHANNEL_COLORS.broadcast_tv} hideClicks={true} onHelpClick={() => handleHelpClick('broadcast_tv')} />
        )}
        {(spotsData?.summary?.sport_signage_impressions > 0) && (
          <MetricCard title="Sport Signage – MLB & NCAA" value={spotsData.summary.sport_signage_impressions} color={CHANNEL_COLORS.sport_signage} hideClicks={true} onHelpClick={() => handleHelpClick('sport_signage')} />
        )}
        {(channelTotals['YouTube'] > 0) && (
          <MetricCard title="YouTube" value={channelTotals['YouTube']} color={CHANNEL_COLORS.youtube} onHelpClick={() => handleHelpClick('youtube')} />
        )}
        {(channelTotals['Paid Social'] > 0) && (
          <MetricCard title="Paid Social" value={channelTotals['Paid Social']} color={CHANNEL_COLORS.paid_social} onHelpClick={() => handleHelpClick('paid_social')} />
        )}
        {(channelTotals['Streaming Audio'] > 0) && (
          <MetricCard title="Streaming Audio" value={channelTotals['Streaming Audio']} color={CHANNEL_COLORS.streaming_audio} hideClicks={true} onHelpClick={() => handleHelpClick('streaming_audio')} />
        )}
        {(channelTotals['Radio'] > 0) && (
          <MetricCard title="Radio" value={channelTotals['Radio']} color={CHANNEL_COLORS.radio} hideClicks={true} onHelpClick={() => handleHelpClick('radio')} />
        )}
        {(selectedData.clicks > 0) && (
          <MetricCard
            title="Total Clicks"
            value={selectedData.clicks || 0}
            icon={<i className="material-icons">touch_app</i>}
            onHelpClick={() => handleHelpClick('total_clicks')}
          />
        )}
        {(selectedData.store_visits > 0) && (
          <MetricCard
            title="Store Visits"
            value={selectedData.store_visits || 0}
            icon={<i className="material-icons">store</i>}
            onHelpClick={() => handleHelpClick('store_visits')}
          />
        )}
        {(selectedData.clicks > 0 || selectedData.impressions > 0) && (
          <MetricCard
            title="Avg CTR"
            value={selectedData.ctr || 0}
            icon={<i className="material-icons">percent</i>}
            onHelpClick={() => handleHelpClick('avg_ctr')}
          />
        )}
        {mlbSocialData && mlbSocialData.views > 0 && (
          <MetricCard title="MLB National Social – Views" value={mlbSocialData.views} color="#1877F2" hideClicks={true} onHelpClick={() => handleHelpClick('mlb_social_views')} />
        )}
        {mlbSocialData && mlbSocialData.engagement > 0 && (
          <MetricCard title="MLB National Social – Engagement" value={mlbSocialData.engagement} color="#1DA1F2" hideClicks={true} onHelpClick={() => handleHelpClick('mlb_social_engagement')} />
        )}
      </div>

      {viewMode === 'table' && districtData.length > 0 && (
        <div className="table-section">
          <h2>District Performance</h2>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>District</th>
                  <th>Manager</th>
                  <th>Dealers</th>
                  <th>Display</th>
                  <th>DOOH</th>
                  <th>Streaming TV</th>
                  <th>Streaming Audio</th>
                  <th>Broadcast TV</th>
                  <th>Radio</th>
                  <th>Sport Signage</th>
                  <th>Store Visits</th>
                </tr>
              </thead>
              <tbody>
                {districtData.map(district => {
                  const ch = district.channels || {};
                  const fmt = (v: number | undefined) => v && v > 0
                    ? formatNumber(v)
                    : <span style={{ color: '#999' }}>—</span>;
                  return (
                  <tr key={district.manager} className={selectedDistrict === district.manager ? 'selected' : ''}>
                    <td className="district-name">
                      <div className="label-with-action">
                        <span>{district.name}</span>
                        <button className="table-action-btn primary" onClick={() => navigateToDistrict(district.manager)}>View District</button>
                      </div>
                    </td>
                    <td>{district.manager}</td>
                    <td>{district.dealerCount}</td>
                    <td>{fmt(ch['Display'])}</td>
                    <td>{fmt(ch['DOOH'])}</td>
                    <td>{fmt(ch['Streaming TV'])}</td>
                    <td>{fmt(ch['Streaming Audio'])}</td>
                    <td>{fmt(ch['Broadcast TV'])}</td>
                    <td>{fmt(ch['Radio'])}</td>
                    <td>{fmt(ch['Sport Signage'])}</td>
                    <td>{district.store_visits > 0 ? formatNumber(district.store_visits) : <span style={{ color: '#999' }}>—</span>}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewMode === 'map' && (
        <div className="map-container">
          <h2>Regional Coverage Map</h2>
          <MapView
            data={mapData}
            viewLevel="region"
            dealerLocations={dealerLocations}
          />
        </div>
      )}

      {viewMode === 'chart' && (
        <div className="charts-section">
          <h2>Regional Channel Performance</h2>
          <LazyLoad fallback={<div className="chart-placeholder">Loading charts...</div>}>
            <div className="charts-grid">
              {hasChartData(chartData.display) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.display}
                    title="Display Performance"
                    color={CHANNEL_COLORS.display}
                  />
                </div>
              )}
              {hasChartData(chartData.dooh) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.dooh}
                    title="DOOH Performance"
                    color={CHANNEL_COLORS.dooh}
                  />
                </div>
              )}
              {hasChartData(chartData.streaming_tv) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.streaming_tv}
                    title="Streaming TV Performance"
                    color={CHANNEL_COLORS.streaming_tv}
                    showClicks={false}
                  />
                </div>
              )}
              {hasChartData(chartData.youtube) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.youtube}
                    title="YouTube Performance"
                    color={CHANNEL_COLORS.youtube}
                  />
                </div>
              )}
              {hasChartData(chartData.search) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.search}
                    title="Search Performance"
                    color={CHANNEL_COLORS.search}
                  />
                </div>
              )}
              {hasChartData(chartData.broadcast_tv) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.broadcast_tv}
                    title="Broadcast TV Performance"
                    color={CHANNEL_COLORS.broadcast_tv}
                    showClicks={false}
                  />
                </div>
              )}
              {hasChartData(chartData.sport_signage) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.sport_signage}
                    title="Sport Signage Performance"
                    color={CHANNEL_COLORS.sport_signage}
                    showClicks={false}
                  />
                </div>
              )}
              {hasChartData(chartData.streaming_audio) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.streaming_audio}
                    title="Streaming Audio Performance"
                    color={CHANNEL_COLORS.streaming_audio}
                    showClicks={false}
                  />
                </div>
              )}
              {hasChartData(chartData.radio) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.radio}
                    title="Radio Performance"
                    color={CHANNEL_COLORS.radio}
                    showClicks={false}
                  />
                </div>
              )}
              {hasChartData(chartData.paid_social) && (
                <div className="chart-container">
                  <ComboChart
                    data={chartData.paid_social}
                    title="Paid Social Performance"
                    color={CHANNEL_COLORS.paid_social}
                  />
                </div>
              )}
            </div>
          </LazyLoad>

          {/* TV/Radio Spots Tables */}
          {spotsData && (
            <LazyLoad fallback={<div className="lazy-placeholder">Loading tables...</div>}>
              {hasTableData(spotsData.television) && (
                <section className="television-section">
                  <TelevisionTable
                    data={spotsData.television || []}
                    onHelpClick={() => handleHelpClick('broadcast_tv')}
                  />
                  {spotsData.summary && (
                    <div className="dma-info" style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                      Showing TV spots for {spotsData.summary.unique_dmas || 0} unique DMAs across region
                    </div>
                  )}
                </section>
              )}

              {hasTableData(spotsData.radio) && (
                <section className="radio-section">
                  <RadioTable
                    data={spotsData.radio || []}
                    onHelpClick={() => handleHelpClick('radio')}
                  />
                  {spotsData.summary && spotsData.summary.radio_impressions > 0 && (
                    <div className="dma-info" style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                      Showing Radio spots for {spotsData.summary.unique_dmas || 0} unique DMAs across region
                    </div>
                  )}
                </section>
              )}

              {hasTableData(spotsData.sport_signage) && (
                <section className="sport-signage-section">
                  <SportSignageTable
                    data={spotsData.sport_signage || []}
                    onHelpClick={() => handleHelpClick('sport_signage')}
                  />
                </section>
              )}
            </LazyLoad>
          )}
        </div>
      )}

        </div>
      </div>

    </div>
      )
    },
    {
      id: 'management',
      label: 'Team Management',
      icon: 'people',
      content: (
        <UserManagementView
          currentUserRole={user?.role}
          scopeMessage={`Showing district managers and dealers in ${user?.manager_name || 'your'} region (automatically filtered by backend)`}
        />
      )
    },
    {
      id: 'coverage',
      label: 'Coverage Analysis',
      icon: 'analytics',
      content: (
        <CoverageAnalysisView
          hierarchicalScope="regional"
          managerName={user?.manager_name}
          scopeMessage={`Showing coverage analysis for ${user?.manager_name || 'your'} regional dealers`}
        />
      )
    }
  ];

  return (
    <div className="regional-manager-dashboard-wrapper">
      <KnowledgePanel
        isOpen={knowledgePanelOpen}
        onClose={() => setKnowledgePanelOpen(false)}
        topic={currentTopic}
        title={getDefinition(currentTopic)?.title || ''}
        description={getDefinition(currentTopic)?.description || ''}
      />
      <DashboardHeader
        title="Regional Manager Dashboard"
        subtitle={user?.manager_name || user?.username}
        badges={[
          { label: `${regionalMetrics.districtCount} Districts`, variant: 'primary' },
          { label: `${regionalMetrics.dealerCount} Total Dealers`, variant: 'secondary' }
        ]}
      />
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
};

export default React.memo(RegionalManagerDashboard);
